import React, { ReactNode, SyntheticEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import ArrowForwardIosOutlined from '@mui/icons-material/ArrowForwardIosOutlined';
import DashboardCustomizeOutlined from '@mui/icons-material/DashboardCustomizeOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';

import { IoMdLink, IoMdGrid, IoIosListBox } from 'react-icons/io';
import { FaTruckLoading } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';

import ROUTES from '../../../../../../core/constants/routes.constants';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import {
  exportIsBusySelector,
  exportResponseSelector,
  getSampleExportThunk,
} from '../../../../../../store/cryoboxList';
import { ReportExportStatus } from '../../../../../../core/api/lab.types';
import ExportReportButton from '../../../Storage/scenes/List/components/ExportReportButton';

interface ManagementOption {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: ReactNode;
}

interface ManagementCardProps {
  option: ManagementOption;
}

interface TabPanelProps {
  children: ReactNode;
  value: number;
  index: number;
}

const managementOptions: ManagementOption[] = [
  {
    id: 'sample-processing-option',
    title: 'Sample Processing',
    description: 'Create and manage sample processing batches.',
    route: ROUTES.batchesCreate,
    icon: <IoMdGrid size={26} />,
  },
  {
    id: 'sample-storage-option',
    title: 'Sample Storage',
    description: 'Create cryoboxes and manage sample storage.',
    route: ROUTES.cryoboxCreate,
    icon: <IoIosListBox size={26} />,
  },
  {
    id: 'sample-linking-option',
    title: 'RGC Linking',
    description: 'Link samples and RGC barcodes for downstream workflows.',
    route: ROUTES.linking,
    icon: <IoMdLink size={26} />,
  },
  {
    id: 'sample-shipping-option',
    title: 'Ship Sample Boxes',
    description: 'Prepare sample boxes for shipment and manifesting.',
    route: ROUTES.shippingCreate,
    icon: <FaTruckLoading size={26} />,
  },
  {
    id: 'sample-shipments-option',
    title: 'Shipping History',
    description: 'Review previous shipments and shipment manifests.',
    route: ROUTES.shippingHistory,
    icon: <RiFileList3Line size={26} />,
  },
];

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      id={`home-options-tabpanel-${index}`}
      aria-labelledby={`home-options-tab-${index}`}
      sx={{ p: { xs: 2.5, md: 3 } }}
    >
      {children}
    </Box>
  );
}

function ManagementCard({ option }: ManagementCardProps) {
  return (
    <ButtonBase
      component={Link}
      color="primary"
      data-testid={option.id}
      to={option.route}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        borderRadius: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          border: 1,
          borderColor: 'primary.main',
          overflow: 'hidden',
          backgroundColor: 'primary.main',
          color: 'white',
          transition: 'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            borderColor: 'primary.light',
            transform: 'translateY(-2px)',
            backgroundColor: 'primary.dark',
            boxShadow: 4,
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            {option.icon}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
              {option.title}
            </Typography>

            <Typography variant="body2" component="p" color="text.white">
              {option.description}
            </Typography>
          </Box>

          <ArrowForwardIosOutlined color="inherit" sx={{ fontSize: 16, flexShrink: 0 }} />
        </Box>
      </Paper>
    </ButtonBase>
  );
}

function HomeOptions() {
  const dispatch = useAppDispatch();
  const exportLoading = useAppSelector(exportIsBusySelector);
  const exportResponse = useAppSelector(exportResponseSelector);
  const [exportStatus, setExportStatus] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const createExport = (type: ReportExportStatus) => {
    if (!exportLoading) {
      setExportStatus(type);
      dispatch(getSampleExportThunk(type));
    }
  };

  const downloadExport = () => {
    if (exportResponse?.exportLink) {
      window.open(exportResponse.exportLink, '_blank');
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          pt: { xs: 2.5, md: 3 },
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
          Laboratory Workflows
        </Typography>

        <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
          Access common laboratory tasks, shipment workflows and operational reports.
        </Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          '& .MuiTab-root': {
            minHeight: 56,
            fontWeight: 700,
            textTransform: 'none',
          },
        }}
      >
        <Tab
          id="home-options-tab-0"
          aria-controls="home-options-tabpanel-0"
          icon={<DashboardCustomizeOutlined />}
          iconPosition="start"
          label="Management"
        />

        <Tab
          id="home-options-tab-1"
          aria-controls="home-options-tabpanel-1"
          icon={<FileDownloadOutlined />}
          iconPosition="start"
          label="Download Reports"
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box mb={2}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
            Management
          </Typography>

          <Typography variant="body2" component="p" color="text.secondary">
            Common laboratory workflows and operational tasks.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {managementOptions.map((option) => (
            <Grid item xs={12} md={6} key={option.id}>
              <ManagementCard option={option} />
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box mb={2.5}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
            Download Reports
          </Typography>

          <Typography variant="body2" component="p" color="text.secondary">
            Generate shipment reports for sequencing and storage workflows.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ExportReportButton
              exportResponse={exportResponse}
              exportStatus={exportStatus}
              downloadExport={downloadExport}
              createExport={createExport}
              exportLoading={exportLoading}
              buttonType={ReportExportStatus.sequencing}
              buttonCreateLabel="Sequencing Shipment Report"
              buttonDownloadLabel="Sequencing Shipment Report"
              buttonTestId="downloadSequencerReport"
              description="WHOLE BLOOD sample locations and shipment eligibility."
              tooltip="Download locations of WHOLE BLOOD samples for sequencing shipment. Eligibility requires EHR received + enrolled; duplicates, orphan samples, biobanked aliquots, Do Not Ship, and withdrawn samples are marked ineligible."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ExportReportButton
              exportResponse={exportResponse}
              exportStatus={exportStatus}
              downloadExport={downloadExport}
              createExport={createExport}
              exportLoading={exportLoading}
              buttonType={ReportExportStatus.storage}
              buttonCreateLabel="Storage Shipment Report"
              buttonDownloadLabel="Storage Shipment Report"
              buttonTestId="downloadStorageReport"
              description="PLASMA and SERUM sample locations for storage shipment."
              tooltip="Download locations of PLASMA and SERUM samples for storage shipment. Eligibility based on enrolment status, with withdrawn samples included but marked ineligible."
            />
          </Grid>
        </Grid>
      </TabPanel>
    </Paper>
  );
}

export default HomeOptions;
